/**
 * Type definitions for Processo Parceiro (Portal de Substabelecidos)
 *
 * Story: 11.5 - Processos Atribuídos
 * IMPORTANT: Keep in sync with backend schemas in:
 * app/schemas/processo_parceiro.py
 */

// ==================== Enums ====================

export enum ProcessoStatus {
  AGUARDANDO_DOCUMENTOS = "aguardando_documentos",
  EM_ANALISE = "em_analise",
  AGUARDANDO_RETORNO = "aguardando_retorno",
  EM_ANDAMENTO = "em_andamento",
  CONCLUIDO = "concluido",
  CANCELADO = "cancelado",
}

export enum ProcessoPrioridade {
  BAIXA = "baixa",
  NORMAL = "normal",
  ALTA = "alta",
  URGENTE = "urgente",
}

export enum ProximaAcaoTipo {
  UPLOAD_DOCUMENTO = "upload_documento",
  ASSINATURA = "assinatura",
  AGUARDAR_RETORNO = "aguardar_retorno",
  FINALIZAR = "finalizar",
  PAGAMENTO = "pagamento",
}

export enum ProcessoEventType {
  CRIADO = "criado",
  ATRIBUIDO = "atribuido",
  DOCUMENTO_ADICIONADO = "documento_adicionado",
  PRAZO_ALTERADO = "prazo_alterado",
  COMENTARIO_ADICIONADO = "comentario_adicionado",
  STATUS_ALTERADO = "status_alterado",
  PAGAMENTO_ADICIONADO = "pagamento_adicionado",
}

export enum PagamentoStatus {
  PENDENTE = "pendente",
  PAGO = "pago",
  ATRASADO = "atrasado",
  CANCELADO = "cancelado",
}

// ==================== Main Interfaces ====================

export interface ProcessoParceiro {
  id: string;
  company_id: string;
  substabelecido_id: string;
  numero: string;
  titulo: string;
  descricao: string | null;
  status: ProcessoStatus;
  prioridade: ProcessoPrioridade;
  proxima_acao_tipo: ProximaAcaoTipo | null;
  proxima_acao_descricao: string | null;
  proxima_acao_prazo: string | null; // ISO datetime string
  is_prazo_vencido: boolean;
  dias_ate_prazo: number | null;
  assigned_to: string | null; // UUID
  substabelecido_razao_social: string | null;
  assigned_to_name: string | null;
  created_by_name: string | null;
  total_documentos: number;
  total_pagamentos: number;
  total_timeline_events: number;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
}

export interface ProcessoListItem {
  id: string;
  numero: string;
  titulo: string;
  status: ProcessoStatus;
  prioridade: ProcessoPrioridade;
  proxima_acao_descricao: string | null;
  proxima_acao_prazo: string | null;
  is_prazo_vencido: boolean;
  dias_ate_prazo: number | null;
  created_at: string;
}

export interface ProcessoListResponse {
  items: ProcessoListItem[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface ProcessoFilterParams {
  status?: ProcessoStatus;
  prazo_ate?: string; // ISO date string (YYYY-MM-DD)
  prazo_vencido?: boolean;
  prioridade?: ProcessoPrioridade;
}

// ==================== Timeline & Events ====================

export interface TimelineEvent {
  id: string;
  event_type: ProcessoEventType;
  description: string;
  metadata: Record<string, any>;
  created_by_name: string | null;
  created_at: string; // ISO datetime
}

export interface TimelineListResponse {
  items: TimelineEvent[];
  total: number;
  page: number;
  pages: number;
}

// ==================== Pagamentos ====================

export interface Pagamento {
  id: string;
  processo_id: string | null;
  valor: number; // Decimal as number
  data_pagamento: string | null; // ISO date or null
  data_vencimento: string | null; // ISO date or null
  descricao: string | null;
  status: PagamentoStatus;
  numero_nota_fiscal: string | null;
  numero_boleto: string | null;
  created_at: string; // ISO datetime
}

export interface PagamentosByProcessoResponse {
  pagamentos: Pagamento[];
  total_pago: number;
  total_pendente: number;
}

// ==================== Requests ====================

export interface AddDocumentoRequest {
  document_id: string; // UUID
  upload_reason?: string;
  is_required?: boolean;
}

// ==================== Component Props ====================

export interface ProcessoListCardProps {
  processo: ProcessoListItem;
  onClick: (processo: ProcessoListItem) => void;
}

export interface ProcessoFilterPanelProps {
  onFilterChange: (filters: ProcessoFilterParams) => void;
  initialFilters?: ProcessoFilterParams;
}

export interface ProcessoDetailsModalProps {
  processoId: string;
  isOpen: boolean;
  onClose: () => void;
}

export interface PrazoIndicatorProps {
  prazo: string | null; // ISO datetime
  status: ProcessoStatus;
  isPrazoVencido: boolean;
  diasAtePrazo: number | null;
  showCountdown?: boolean;
}

export interface ProcessoTimelineListProps {
  processoId: string;
}

export interface ProcessoDocumentosListProps {
  processoId: string;
}

export interface ProcessoPagamentosListProps {
  processoId: string;
}

// ==================== Status & Priority Labels ====================

export const PROCESSO_STATUS_LABELS: Record<ProcessoStatus, string> = {
  [ProcessoStatus.AGUARDANDO_DOCUMENTOS]: "Aguardando Documentos",
  [ProcessoStatus.EM_ANALISE]: "Em Análise",
  [ProcessoStatus.AGUARDANDO_RETORNO]: "Aguardando Retorno",
  [ProcessoStatus.EM_ANDAMENTO]: "Em Andamento",
  [ProcessoStatus.CONCLUIDO]: "Concluído",
  [ProcessoStatus.CANCELADO]: "Cancelado",
};

export const PROCESSO_STATUS_COLORS: Record<ProcessoStatus, string> = {
  [ProcessoStatus.AGUARDANDO_DOCUMENTOS]: "warning",
  [ProcessoStatus.EM_ANALISE]: "info",
  [ProcessoStatus.AGUARDANDO_RETORNO]: "secondary",
  [ProcessoStatus.EM_ANDAMENTO]: "primary",
  [ProcessoStatus.CONCLUIDO]: "success",
  [ProcessoStatus.CANCELADO]: "default",
};

export const PRIORIDADE_LABELS: Record<ProcessoPrioridade, string> = {
  [ProcessoPrioridade.BAIXA]: "Baixa",
  [ProcessoPrioridade.NORMAL]: "Normal",
  [ProcessoPrioridade.ALTA]: "Alta",
  [ProcessoPrioridade.URGENTE]: "Urgente",
};

export const PRIORIDADE_COLORS: Record<ProcessoPrioridade, string> = {
  [ProcessoPrioridade.BAIXA]: "success",
  [ProcessoPrioridade.NORMAL]: "default",
  [ProcessoPrioridade.ALTA]: "warning",
  [ProcessoPrioridade.URGENTE]: "error",
};

export const PAGAMENTO_STATUS_LABELS: Record<PagamentoStatus, string> = {
  [PagamentoStatus.PENDENTE]: "Pendente",
  [PagamentoStatus.PAGO]: "Pago",
  [PagamentoStatus.ATRASADO]: "Atrasado",
  [PagamentoStatus.CANCELADO]: "Cancelado",
};

export const PAGAMENTO_STATUS_COLORS: Record<PagamentoStatus, string> = {
  [PagamentoStatus.PENDENTE]: "warning",
  [PagamentoStatus.PAGO]: "success",
  [PagamentoStatus.ATRASADO]: "error",
  [PagamentoStatus.CANCELADO]: "default",
};
